import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileText, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { FileUpload } from '../api/postAPI';
import Alert from '../components/alert';

interface UploadedFile {
    id: string;
    name: string;
    size: number;
    content: string;
    status: 'pending' | 'uploading' | 'success' | 'error';
    file: File;
    error?: string;
    uploadResponse?: any;
}

interface Alerts {
    code: number;
    message: string;
}

const HTMLFileUpload = () => {
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [error, setError] = useState<Alerts>({ code: -1 , message: '' });
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const validateFile = (file: File) => {
        if (file.type !== 'text/html' && !file.name.toLowerCase().endsWith('.html')) {
            showAlert(1, `"${file.name}" is not a valid HTML file. Only .html files are allowed.`);
            throw new Error(`"${file.name}" is not a valid HTML file. Only .html files are allowed.`);
        }
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            showAlert(1, `"${file.name}" is too large. File size must be less than 10MB.`);
            throw new Error(`"${file.name}" is too large. File size must be less than 10MB.`);
        }
        if (file.size === 0) {
            showAlert(1, `"${file.name}" is empty. Please select a valid HTML file.`);
            throw new Error(`"${file.name}" is empty. Please select a valid HTML file.`);
        }
    };

    const handleFiles = useCallback(async (fileList: FileList) => {
        const allFiles = Array.from(fileList);
        const htmlFiles = allFiles.filter(file =>
            file.type === 'text/html' || file.name.toLowerCase().endsWith('.html')
        );

        if (htmlFiles.length === 0) {
            return;
        }

        // Add files to pending list
        const newFiles: UploadedFile[] = htmlFiles.map(file => ({
            id: Math.random().toString(36).substr(2, 9),
            name: file.name,
            size: file.size,
            content: '',
            status: 'pending' as const,
            file: file
        }));

        setFiles(prev => [...prev, ...newFiles]);
    }, []);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.currentTarget === e.target) {
            setIsDragOver(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const { files } = e.dataTransfer;
        if (files.length > 0) {
            handleFiles(files);
        }
    }, [handleFiles]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        if (files && files.length > 0) {
            handleFiles(files);
        }
        // Reset input value to allow selecting the same file again
        e.target.value = '';
    }, [handleFiles]);

    const handleUpload = async () => {
        const pendingFiles = files.filter(f => f.status === 'pending');

        if (pendingFiles.length === 0) {
            return;
        }

        setIsUploading(true);

        // Process each pending file
        for (const pendingFile of pendingFiles) {
            // Set to uploading status
            setFiles(prev => prev.map(f =>
                f.id === pendingFile.id ? { ...f, status: 'uploading' as const } : f
            ));

            try {
                validateFile(pendingFile.file);
                const response = await FileUpload(pendingFile.file);
                if (response === "Success" || response?.status === "success" || response?.message === "Success") {
                    showAlert(0, `"${pendingFile.file.name}" uploaded successfully`);
                }
                setFiles(prev => prev.map(f =>
                    f.id === pendingFile.id
                        ? {
                            ...f,
                            status: 'success' as const,
                            content: response?.message || `"${pendingFile.file.name}" uploaded successfully`,
                            uploadResponse: response
                        }
                        : f
                ));
            } catch (error) {
                showAlert(1, `Upload failed for file ${pendingFile.file.name}:`);
                console.error(`Upload failed for file ${pendingFile.file.name}:`, error);

                // Update file status to error with error message
                setFiles(prev => prev.map(f =>
                    f.id === pendingFile.id
                        ? {
                            ...f,
                            status: 'error' as const,
                            error: error instanceof Error ? error.message : 'Upload failed'
                        }
                        : f
                ));
            }
        }

        setIsUploading(false);
    };

    const removeFile = (id: string) => {
        setFiles(prev => prev.filter(file => file.id !== id));
    };

    const clearAll = () => {
        setFiles([]);
    };

    const pendingFilesCount = files.filter(f => f.status === 'pending').length;

    const showAlert = (code: number, message: string) => {
        setError({ code: code, message: message });
    }

    const onClose = () => {
        setError({ code: -1, message: '' });
    }

    return (
        <div className="w-full max-w-4xl mx-auto p-6" style={{ fontFamily: '"Times New Roman", serif' }}>
            <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-3xl p-8 shadow-2xl">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: '"Times New Roman", serif' }}>HTML File Upload</h2>
                    <p className="text-slate-300" style={{ fontFamily: '"Times New Roman", serif' }}>Drag and drop your HTML files or click to browse</p>
                </div>

                {/* Upload Area */}
                <div
                    className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer group ${isDragOver
                        ? 'border-purple-400 bg-purple-500/10 scale-105'
                        : 'border-slate-600 hover:border-purple-500 hover:bg-slate-800/30'
                        }`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".html,text/html"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    <div className={`transition-all duration-300 ${isDragOver ? 'scale-110' : ''}`}>
                        <div className="mb-6">
                            <Upload className={`w-16 h-16 mx-auto transition-colors duration-300 ${isDragOver ? 'text-purple-400' : 'text-slate-400 group-hover:text-purple-500'
                                }`} />
                        </div>

                        <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: '"Times New Roman", serif' }}>
                            {isDragOver ? 'Drop your HTML files here!' : 'Upload HTML Files'}
                        </h3>

                        <p className="text-slate-400 mb-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                            Drag and drop files here or click to browse
                        </p>

                        <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-white font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200" style={{ fontFamily: '"Times New Roman", serif' }}>
                            Choose Files
                        </div>

                        <p className="text-xs text-slate-500 mt-4" style={{ fontFamily: '"Times New Roman", serif' }}>
                            Supports: .html files • Max size: 10MB per file
                        </p>
                    </div>

                    {/* Loading overlay */}
                    {isUploading && (
                        <div className="absolute inset-0 bg-slate-900/80 rounded-2xl flex items-center justify-center">
                            <div className="text-center">
                                <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                                <p className="text-white font-medium" style={{ fontFamily: '"Times New Roman", serif' }}>Processing files...</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* File List */}
                {files.length > 0 && (
                    <div className="mt-8">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: '"Times New Roman", serif' }}>
                                Selected Files ({files.length})
                            </h3>
                            <button
                                onClick={clearAll}
                                className="text-slate-400 hover:text-red-400 text-sm font-medium transition-colors"
                                style={{ fontFamily: '"Times New Roman", serif' }}
                            >
                                Clear All
                            </button>
                        </div>

                        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 mb-6">
                            <div className="h-fit overflow-x-auto overflow-y-hidden">
                                <div className="flex space-x-3 h-full">
                                    {files.map((file) => (
                                        <div
                                            key={file.id}
                                            className="bg-slate-700/50 rounded-lg p-3 border border-slate-600/50 flex-shrink-0 flex flex-col justify-between"
                                            style={{ width: '120px', height: '50%' }}
                                        >
                                            <div>
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center space-x-1">
                                                        <div className="flex-shrink-0">
                                                            {file.status === 'pending' && (
                                                                <div className="w-3 h-3 bg-yellow-500 rounded-full flex items-center justify-center">
                                                                    <div className="w-1 h-1 bg-yellow-200 rounded-full"></div>
                                                                </div>
                                                            )}
                                                            {file.status === 'uploading' && (
                                                                <div className="w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {file.status === 'success' && (
                                                                <CheckCircle2 className="w-3 h-3 text-green-400" />
                                                            )}
                                                            {file.status === 'error' && (
                                                                <AlertCircle className="w-3 h-3 text-red-400" />
                                                            )}
                                                        </div>

                                                        <FileText className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                                    </div>

                                                    <button
                                                        onClick={() => removeFile(file.id)}
                                                        className="p-0.5 text-slate-400 hover:text-red-400 transition-colors"
                                                    >
                                                        <X className="w-2.5 h-2.5" />
                                                    </button>
                                                </div>

                                                <div className="mb-2">
                                                    <p className="text-white text-xs font-medium mb-1 break-words leading-tight line-clamp-2" style={{ fontFamily: '"Times New Roman", serif' }}>
                                                        {file.name}
                                                    </p>
                                                    <p className="text-slate-400 text-xs mb-1" style={{ fontFamily: '"Times New Roman", serif' }}>
                                                        {formatFileSize(file.size)}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-xs font-medium" style={{ fontFamily: '"Times New Roman", serif' }}>
                                                {file.status === 'pending' && (
                                                    <span className="text-yellow-400">Ready</span>
                                                )}
                                                {file.status === 'uploading' && (
                                                    <span className="text-purple-400">Uploading...</span>
                                                )}
                                                {file.status === 'success' && (
                                                    <span className="text-green-400">Complete</span>
                                                )}
                                                {file.status === 'error' && (
                                                    <span className="text-red-400">Failed</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Upload Button */}
                        {pendingFilesCount > 0 && (
                            <div className="text-center">
                                <button
                                    onClick={handleUpload}
                                    disabled={isUploading}
                                    className={`inline-flex items-center px-8 py-4 rounded-full font-semibold text-white shadow-lg transform transition-all duration-200 ${isUploading
                                        ? 'bg-slate-600 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-green-600 to-blue-600 hover:shadow-xl hover:scale-105'
                                        }`}
                                    style={{ fontFamily: '"Times New Roman", serif' }}
                                >
                                    {isUploading ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-5 h-5 mr-3" />
                                            Upload {pendingFilesCount} File{pendingFilesCount !== 1 ? 's' : ''}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <Alert code={error.code} message={error.message} onClose={onClose} />
        </div>
    );
};

export default HTMLFileUpload;